import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { folderService } from '../services/folderService';
import { on } from '../realtime/realtime';
import toast from 'react-hot-toast';
import useStorageStore from '../stores/storageStore';
import { compressImage } from '../lib/ImageConversion';

export const useFolders = () => {
  const queryClient = useQueryClient();

  const { data: folders = [], isLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: () => folderService.getFolders(),
  });

  const { mutateAsync: createFolder, isPending: isCreating } = useMutation({
    mutationFn: ({ name, retentionPeriod = 7776000 }) => folderService.createFolder(name, retentionPeriod),
    onSuccess: (folder) => {
      queryClient.setQueryData(['folders'], (prev = []) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.some((f) => f.id === folder.id) ? list : [...list, folder];
      });
      toast.success('Folder created');
    },
    onError: () => toast.error('Failed to create folder'),
  });

  const { mutateAsync: updateFolder } = useMutation({
    mutationFn: ({ folderId, name, retentionPeriod }) => folderService.updateFolder(folderId, { name, retentionPeriod }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['folders'], (prev = []) =>
        (Array.isArray(prev) ? prev : []).map((f) => f.id === updated.id ? { ...f, ...updated } : f)
      );
      toast.success('Folder updated');
    },
    onError: (err) => {
      const msg = err?.response?.data?.error?.message || 'Failed to update folder';
      toast.error(msg);
    },
  });

  // deleteFolder throws on error so callers can handle the message themselves
  const { mutateAsync: deleteFolder } = useMutation({
    mutationFn: (folderId) => folderService.deleteFolder(folderId),
    onSuccess: (_, folderId) => {
      queryClient.setQueryData(['folders'], (prev = []) =>
        (Array.isArray(prev) ? prev : []).filter((f) => f.id !== folderId)
      );
      toast.success('Folder deleted');
    },
    // No onError toast — callers render the error inline in the modal
  });

  return { folders, isLoading, createFolder, isCreating, deleteFolder, updateFolder };
};

// ---

// Active or trashed items for a folder.
// useItems(folderId)                 — active items in a specific folder
// useItems(null)                     — active items across all folders
// useItems(null, { trashed: true })  — trash bin
export const useItems = (folderId, { trashed = false } = {}) => {
  const queryClient = useQueryClient();
  const { updateStorageUsage } = useStorageStore();

  const queryKey = useMemo(
    () => ['items', folderId ?? 'all', trashed ? 'trashed' : 'active'],
    [folderId, trashed]
  );

  const { data: items = [], isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      trashed
        ? folderService.getTrashedItems({ signal })
        : folderService.getFolderItemsWithContent(folderId, { signal }),
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['items'] }),
    [queryClient]
  );

  useEffect(() => {
    const off = [
      on('clipboard_item_created', invalidate),
      on('clipboard_item_deleted', invalidate),
      on('clipboard_item_updated', invalidate),
      on('item_pin_updated', invalidate),
    ];
    return () => off.forEach((fn) => fn());
  }, [invalidate]);

  const { mutateAsync: createItem } = useMutation({
    mutationFn: async (arg) => {
      const blob = arg?.blob ?? arg;
      const targetFolderId = arg?.folderId ?? folderId;
      const compressed = blob.type?.startsWith('image/') ? await compressImage(blob) : blob;
      const item = await folderService.createItem(targetFolderId, compressed);
      await updateStorageUsage();
      return item;
    },
    onSuccess: invalidate,
    onError: () => toast.error('Failed to create item'),
  });

  const { mutateAsync: removeItem } = useMutation({
    mutationFn: (itemId) => folderService.trashItem(itemId),
    onSuccess: () => {
      invalidate();
      toast.success('Item moved to trash');
    },
    onError: () => toast.error('Failed to move item to trash'),
  });

  const { mutateAsync: restoreItem } = useMutation({
    mutationFn: (itemId) => folderService.restoreItem(itemId),
    onSuccess: () => {
      invalidate();
      toast.success('Item restored');
    },
    onError: () => toast.error('Failed to restore item'),
  });

  const { mutateAsync: deleteItem } = useMutation({
    mutationFn: (itemId) => folderService.deleteItem(itemId),
    onSuccess: () => {
      invalidate();
      toast.success('Item permanently deleted');
    },
    onError: () => toast.error('Failed to permanently delete item'),
  });

  return { items, isLoading, createItem, removeItem, restoreItem, deleteItem };
};
